import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DetailsList, DetailsListLayoutMode, SelectionMode, IColumn, CommandBar, ICommandBarItemProps, Text, MessageBar, MessageBarType, Shimmer, ShimmerElementType } from '@fluentui/react';
import { api } from '../services/api';
import type { AgentBox } from '../types';
import StatusBadge from '../components/StatusBadge';
import styles from './Dashboard.module.css';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatCountdown(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hrs = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  return `${hrs}h ${mins}m`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState<AgentBox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadBoxes = useCallback(async () => {
    try {
      const data = await api.listInstances();
      data.sort((a, b) => Number(b.id) - Number(a.id));
      setBoxes(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load instances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoxes();
    const interval = setInterval(loadBoxes, 30_000);
    return () => clearInterval(interval);
  }, [loadBoxes]);

  const columns: IColumn[] = [
    {
      key: 'id', name: 'Name', fieldName: 'id', minWidth: 80, maxWidth: 120,
      onRender: (item: AgentBox) => (
        <Link to={`/instances/${item.id}`} style={{ color: 'var(--ab-text-link)', textDecoration: 'none', fontWeight: 600 }}>
          #{item.id}
        </Link>
      ),
    },
    {
      key: 'status', name: 'Status', fieldName: 'status', minWidth: 80, maxWidth: 120,
      onRender: (item: AgentBox) => <StatusBadge status={item.status} />,
    },
    {
      key: 'owner', name: 'Owner', fieldName: 'ownerName', minWidth: 120, maxWidth: 200,
      onRender: (item: AgentBox) => <span>{item.ownerName || item.owner}</span>,
    },
    {
      key: 'created', name: 'Created', fieldName: 'createdAt', minWidth: 140, maxWidth: 180,
      onRender: (item: AgentBox) => <span>{formatDate(item.createdAt)}</span>,
    },
    {
      key: 'expires', name: 'Expires', fieldName: 'expiresAt', minWidth: 80, maxWidth: 120,
      onRender: (item: AgentBox) => <span>{formatCountdown(item.expiresAt)}</span>,
    },
  ];

  const commandBarItems: ICommandBarItemProps[] = [
    { key: 'new', text: 'New', iconProps: { iconName: 'Add' }, onClick: () => { navigate('/spawn'); } },
    { key: 'refresh', text: 'Refresh', iconProps: { iconName: 'Refresh' }, onClick: () => { loadBoxes(); } },
  ];

  return (
    <div className={styles.page}>
      <CommandBar items={commandBarItems} styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }} />

      <div className={styles.viewHeader}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Active Instances</Text>
        <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
          {loading ? 'Loading…' : `${boxes.length} records`}
        </Text>
      </div>

      {error && (
        <div style={{ padding: '0 20px' }}>
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')} isMultiline={false}>{error}</MessageBar>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Shimmer key={i} shimmerElements={[{ type: ShimmerElementType.line, width: '100%', height: 32 }]} />
          ))}
        </div>
      ) : boxes.length === 0 ? (
        <div className={styles.empty}>
          <Text>No instances yet.</Text>
          <Link to="/spawn" style={{ color: 'var(--ab-text-link)' }}>Create your first instance</Link>
        </div>
      ) : (
        <DetailsList
          items={boxes}
          columns={columns}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          onItemInvoked={(item: AgentBox) => navigate(`/instances/${item.id}`)}
          styles={{
            root: { overflow: 'auto', flex: 1 },
            headerWrapper: { position: 'sticky', top: 0, zIndex: 1 },
          }}
        />
      )}
    </div>
  );
}
